<template>
    <a-modal :visible="visible" title="会议邀请" :footer="null" width="520px" @cancel="close">
        <!-- 第一页：会议邀请信息 -->
        <div v-if="step === 1">
            <div class="info-card">
                <div class="info-item">
                    <span class="label">会议名称</span>
                    <span class="value">{{ todoTitle }}</span>
                </div>
                <div class="info-item">
                    <span class="label">内容</span>
                    <span class="value">{{ displayContent || '-' }}</span>
                </div>
                <div class="info-item" v-if="hasLeaveReason">
                    <span class="label">请假原因</span>
                    <span class="value">{{ leaveReason || '-' }}</span>
                </div>
                <div class="info-item">
                    <span class="label">主持人</span>
                    <span class="value">{{ hostName }}</span>
                </div>
                <div class="info-item">
                    <span class="label">时间</span>
                    <span class="value">{{ meetingTimeText }}</span>
                </div>
                <div class="info-item">
                    <span class="label">优先级</span>
                    <span class="value">{{ priorityName }}</span>
                </div>
            </div>
            <div class="btn-group" v-if="isLeaveConfirm">
                <a-button type="primary" size="large" @click="handleAgreeLeave">同意请假</a-button>
                <a-button size="large" @click="handleRejectLeave">拒绝请假</a-button>
            </div>
            <div class="btn-group" v-else>
                <a-button type="primary" size="large" @click="handleConfirm">确认参加</a-button>
                <a-button size="large" @click="goToLeave">申请请假</a-button>
            </div>
        </div>

        <!-- 第二页：请假申请表单 -->
        <div v-if="step === 2">
            <div class="info-card">
                <div class="info-item">
                    <span class="label">会议名称</span>
                    <span class="value">{{ todoTitle }}</span>
                </div>
                <div class="info-item">
                    <span class="label">主持人</span>
                    <span class="value">{{ hostName }}</span>
                </div>
                <div class="info-item">
                    <span class="label">时间</span>
                    <span class="value">{{ meetingTimeText }}</span>
                </div>
            </div>

            <div class="form-area">
                <div class="form-title">请假申请</div>
                <a-form :model="form">
                    <a-form-item label="请假原因" name="reason" :rules="[{ required: true, message: '请输入请假原因' }]">
                        <a-textarea v-model="form.reason" placeholder="客户现场拜访，需要请假" :rows="4" />
                    </a-form-item>
                </a-form>
            </div>

            <div class="btn-group">
                <a-button size="large" @click="step = 1">返回</a-button>
                <a-button type="primary" size="large" @click="handleSubmitLeave">提交申请</a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import { attendeeMeeting, leaveConfirmMeeting } from "@/api/meeting";

export default {
    name: 'Invitation',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        todo: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            // 步骤 1=会议邀请页，2=请假申请页
            step: 1,
            form: {
                reason: ''
            }
        };
    },
    computed: {
        safeTodo() {
            return this.todo || {};
        },
        todoTitle() {
            return this.safeTodo.title || this.safeTodo.content || '未命名';
        },
        isLeaveConfirm() {
            return this.todoTitle.indexOf('会议请假') > -1;
        },
        displayContent() {
            const content = this.safeTodo.content || '';
            const idx = content.indexOf('请假原因');
            return idx > -1 ? content.substring(0, idx) : content;
        },
        leaveReason() {
            const content = this.safeTodo.content || '';
            const idx = content.indexOf('请假原因');
            if (idx === -1) return '';
            return content.substring(idx + 4).replace(/^[:：]\s*/, '');
        },
        hasLeaveReason() {
            return this.leaveReason !== '';
        },
        priorityName() {
            if (!this.safeTodo.priority) return '-';
            const map = { urgent: '紧急', high: '高', medium: '中', low: '低' };
            return map[this.safeTodo.priority] || this.safeTodo.priority;
        },
        deadlineText() {
            if (!this.safeTodo.deadlineDate) return '未设置';
            const date = new Date(this.safeTodo.deadlineDate);
            const pad = n => (n < 10 ? '0' + n : n);
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
        },
        bizInfo() {
            try {
                return JSON.parse(this.safeTodo.bizJson) || {};
            } catch (e) {
                return {};
            }
        },
        hostName() {
            return this.bizInfo.hostName || this.safeTodo.handlerName || '-';
        },
        meetingTimeText() {
            const start = this.bizInfo.startTime;
            const end = this.bizInfo.endTime;
            if (!start) return '-';
            if (!end) return start;
            // 同一天时结束时间只显示时分，如：2026-10-30 17:00至18:00
            const startDate = String(start).split(' ')[0];
            const endDate = String(end).split(' ')[0];
            const endText = endDate === startDate ? String(end).split(' ')[1] || '' : String(end);
            return `${start}至${endText}`;
        }
    },
    watch: {
        visible(val) {
            if (val) {
                this.step = 1;
                this.form.reason = this.leaveReason;
            }
        }
    },
    methods: {
        close() {
            this.$emit('update:visible', false);
        },
        goToLeave() {
            this.form.reason = this.leaveReason;
            this.step = 2;
        },
        // 确认参加
        handleConfirm() {
            attendeeMeeting({
                todoId: this.safeTodo.id,
                attendeeId: this.safeTodo.bizSubId,
                responseState: 'confirmed'
            }).then(() => {
                this.$message.success('确认参加会议');
                this.$emit('refresh');
                this.close();
            });
        },
        // 提交请假申请
        handleSubmitLeave() {
            if (!this.form.reason.trim()) {
                this.$message.warning('请输入请假原因');
                return;
            }
            attendeeMeeting({
                todoId: this.safeTodo.id,
                attendeeId: this.safeTodo.bizSubId,
                responseState: 'leave_applying',
                reason: this.form.reason
            }).then(() => {
                // this.$message.success('提交请假成功');
                this.$emit('refresh');
                this.close();
            });
        },
        // 同意请假
        handleAgreeLeave() {
            this.confirmLeave('agree');
        },
        // 拒绝请假
        handleRejectLeave() {
            this.confirmLeave('reject');
        },
        confirmLeave(confirm) {
            leaveConfirmMeeting({
                attendeeId: this.safeTodo.bizSubId,
                bookInfoId: this.safeTodo.bizId,
                confirm
            }).then(() => {
                // this.$message.success(confirm === 'agree' ? '已同意请假' : '已拒绝请假');
                this.$emit('refresh');
                this.close();
            });
        }
    }
};
</script>

<style scoped>
.info-card {
    background: #f7f9fc;
    border-radius: 12px;
    padding: 24px 20px;
    margin-bottom: 24px;
}
.info-item {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    font-size: 14px;
}
.info-item:last-child {
    margin-bottom: 0;
}
.label {
    color: #888;
    width: 90px;
}
.value {
    color: #222;
    font-weight: 500;
}
.form-area {
    margin-bottom: 22px;
}
.form-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom:16px;
}
.btn-group {
    display: flex;
    gap: 16px;
    justify-content: center;
}
.btn-group :deep(.ant-btn) {
    width: 100px;
    height: 35px;
    font-size:14px;
}
</style>
